import React, { forwardRef, useImperativeHandle } from 'react';
import { arrayOf, bool, func, shape, string, number } from 'prop-types';

import Header from './header';
import FullMap from './full-map';
import { useMapState } from './hooks/use-map-state';
import useMapConfig from './hooks/use-map-config';
import DrawToggler from './lib/controls/draw-toggler';
import DrawBar from './lib/controls/draw-bar';
import { trackClickEvent } from './track';
import { CONTROL_POSITION } from './lib/controls/constants';
import { POLYGON_EVENTS } from './lib';
import colorIcons from '../../constants/components/icons-color';

const { DRAWING } = POLYGON_EVENTS;

const MapMobile = forwardRef(
  (
    {
      fitMarkersInMap,
      className,
      isLoadingResults,
      markers,
      updateResults,
      fixedCoords,
      selectedMarker,
      setSelectedMarker,
      onPolygonError,
    },
    ref,
  ) => {
    const {
      handleDragEnd,
      handleMapLoad,
      handleZoomChanged,
      handleBoundsChanged,
      handleIdle,
      mapStatus,
      polygonTogglerState,
      setPolygonTogglerState,
      handleDrawable,
    } = useMapState({
      fitMarkersInMap,
      isLoadingResults,
      markers,
      updateResults,
      fixedCoords,
      selectedMarker,
      setSelectedMarker,
      onPolygonError,
    });
    const {
      track: {
        openMobileCardCarousel: { analytics, melidata },
      },
    } = useMapConfig();

    const handleMarkerClick = (markerIndex) => {
      trackClickEvent(analytics, melidata);
      setSelectedMarker(markerIndex);
    };

    useImperativeHandle(ref, () => ({
      setPolygonTogglerState,
    }));

    return (
      <FullMap
        className={className}
        mapStatus={mapStatus}
        markers={markers}
        handleBoundsChanged={handleBoundsChanged}
        handleMapLoad={handleMapLoad}
        handleDragEnd={handleDragEnd}
        handleZoomChanged={handleZoomChanged}
        handleMapClick={() => setSelectedMarker(null)}
        handleMarkerClick={handleMarkerClick}
        selectedMarker={selectedMarker}
        handleIdle={handleIdle}
        isLoadingResults={isLoadingResults}
        polygonTogglerState={polygonTogglerState}
      >
        <DrawToggler
          polygonTogglerState={polygonTogglerState}
          handleDrawable={handleDrawable}
          map={mapStatus?.mapInstance}
          position={CONTROL_POSITION.rightBottom}
          iconColor={colorIcons.blue}
        />

        {polygonTogglerState === DRAWING && (
          <DrawBar
            polygonTogglerState={polygonTogglerState}
            handleDrawable={handleDrawable}
            map={mapStatus?.mapInstance}
          />
        )}

        <Header isLoadingResults={isLoadingResults} />
      </FullMap>
    );
  },
);

MapMobile.displayName = 'MapMobile';

MapMobile.propTypes = {
  className: string,
  fitMarkersInMap: bool,
  fixedCoords: arrayOf(shape({ lat: number, lng: number })),
  isLoadingResults: bool,
  markers: arrayOf(shape()),
  selectedMarker: number,
  setSelectedMarker: func.isRequired,
  updateResults: func.isRequired,
  onPolygonError: func,
};

MapMobile.defaultProps = {
  className: null,
  fitMarkersInMap: false,
  fixedCoords: undefined,
  isLoadingResults: false,
  markers: [],
  onPolygonError: undefined,
  selectedMarker: null,
};

export default MapMobile;
